import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/kit";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DEMO = [
  ["micmiccarlos@gmail.com", "Mic — Super Admin"],
  ["chairman@parkingcore.ph", "Chairman — Super Admin (limited ops)"],
  ["manager.mkt@parkingcore.ph", "Branch Manager — Makati"],
  ["accounting@parkingcore.ph", "Head Office Accounting"],
  ["it@parkingcore.ph", "IT"],
  ["ops@parkingcore.ph", "Parking Ops Manager"],
  ["attendant@parkingcore.ph", "Roving Attendant"],
  ["cs@parkingcore.ph", "Customer Service"],
  ["finance@parkingcore.ph", "Finance Staff"],
  ["security@parkingcore.ph", "Security / Viewer"],
];

export default function Login() {
  const { user, signIn } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      signIn(data);
      nav("/dashboard");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#0B0F17] lg:block">
        <img src="https://images.unsplash.com/photo-1604063165585-e17e9c3c3f0b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Parking facility" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/60 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-500 font-mono text-xl font-black text-slate-950">P</div>
            <span className="font-display text-lg font-extrabold tracking-tight">PARKINGCORE</span>
          </div>
          <div className="max-w-lg fade-up">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> Multi-site operations • Philippines
            </div>
            <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">Every gate, lane, peso and barrier — one control room.</h1>
            <p className="mt-4 text-slate-300">Entry → Session → Billing → Payment → Exit → Reconciliation, with audited barrier control across all branches.</p>
          </div>
          <div className="font-mono text-xs text-slate-500">ParkingCore.mistermicpos.co • Asia/Manila</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md fade-up">
          <h2 className="font-display text-3xl font-extrabold tracking-tight">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Authorized ParkingCore personnel only. All access is audited.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <Field label="Email"><Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
            <Field label="Password"><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
            <Button data-testid="login-submit" type="submit" className="w-full bg-amber-500 font-bold text-slate-950 hover:bg-amber-400" disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Sign in
            </Button>
          </form>
          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />OR<div className="h-px flex-1 bg-border" /></div>
          <Button variant="outline" className="w-full" onClick={google} data-testid="login-google">Continue with Google</Button>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">Google sign-in works only for emails already registered by an administrator.</p>
          <div className="mt-8 rounded-lg border border-dashed p-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Demo accounts (password: Parking@2026)</div>
            <div className="grid gap-1">
              {DEMO.map(([em, lb]) => (
                <button key={em} type="button" data-testid={`demo-${em.split("@")[0].replace(".", "-")}`} onClick={() => { setEmail(em); setPassword("Parking@2026"); }}
                  className="flex justify-between rounded px-2 py-1 text-left text-xs hover:bg-muted">
                  <span className="font-medium">{lb}</span><span className="font-mono text-muted-foreground">{em.split("@")[0]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
